import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  viewChildren,
  inject,
  DestroyRef,
} from '@angular/core';
import { ContentContainer } from '../../../../core/content-container/content-container';
import { InViewDirective } from '../../../../shared/in-view/directives/in-view.directive';
import gsap from 'gsap';

export interface Pillar {
  id: string;
  badge: string;
  badgeColorClass: string;
  title: string;
  titleHoverColorClass: string;
  description: string;
  iconClass: string;
  borderHoverClass: string;
  glowColorClass: string;
}

@Component({
  selector: 'app-innovation-pillars',
  standalone: true,
  imports: [ContentContainer, InViewDirective],
  templateUrl: './innovation-pillars.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InnovationPillars {
  readonly pillarCards = viewChildren<ElementRef<HTMLDivElement>>('pillarCard');

  readonly pillars: Pillar[] = [
    {
      id: 'angular',
      badge: 'Angular 20+',
      badgeColorClass: 'bg-primary/15 text-primary border-primary/30',
      title: 'Reatividade de Alta Performance',
      titleHoverColorClass: 'group-hover:text-primary',
      description:
        'Elimine o overhead do Zone.js e projete interfaces ultrarrápidas com Signals, linkedSignal e a nova Resource API para controle de estado cirúrgico e renderização em tempo real.',
      iconClass: 'bi bi-lightning-charge text-primary',
      borderHoverClass:
        'hover:border-primary/60 hover:shadow-[0_0_30px_rgba(102,87,236,0.25)]',
      glowColorClass: 'bg-primary/30',
    },
    {
      id: 'architecture',
      badge: 'Software Design',
      badgeColorClass: 'bg-pink-500/15 text-pink-400 border-pink-500/30',
      title: 'Arquitetura Enterprise & Clean Design',
      titleHoverColorClass: 'group-hover:text-pink-400',
      description:
        'Estruture aplicações complexas preparadas para escala massiva com Domain-Driven Design no frontend, isolamento estrito de domínios e padrões robustos à prova de regressão.',
      iconClass: 'bi bi-diagram-3 text-pink-400',
      borderHoverClass:
        'hover:border-pink-500/60 hover:shadow-[0_0_30px_rgba(236,72,153,0.25)]',
      glowColorClass: 'bg-pink-500/30',
    },
    {
      id: 'monorepo',
      badge: 'Monorepo',
      badgeColorClass: 'bg-sky-400/15 text-sky-400 border-sky-400/30',
      title: 'Monorepos de Alta Escala com Nx',
      titleHoverColorClass: 'group-hover:text-sky-400',
      description:
        'Acelere builds, orquestre múltiplos apps e libs com fronteiras estritas e padronize a criação de código com geradores automatizados.',
      iconClass: 'bi bi-boxes text-sky-400',
      borderHoverClass:
        'hover:border-sky-400/60 hover:shadow-[0_0_30px_rgba(56,189,248,0.25)]',
      glowColorClass: 'bg-sky-400/30',
    },
    {
      id: 'ai-agents',
      badge: 'AI Engineering',
      badgeColorClass:
        'bg-emerald-400/15 text-emerald-400 border-emerald-400/30',
      title: 'Engenharia com Agentes de IA',
      titleHoverColorClass: 'group-hover:text-emerald-400',
      description:
        'Domine Context Engineering, Spec Driven Development e fluxos agenticos autônomos para multiplicar sua velocidade de entrega e elevar a qualidade do software.',
      iconClass: 'bi bi-cpu text-emerald-400',
      borderHoverClass:
        'hover:border-emerald-400/60 hover:shadow-[0_0_30px_rgba(52,211,153,0.25)]',
      glowColorClass: 'bg-emerald-400/30',
    },
  ];

  private destroyRef = inject(DestroyRef);
  private animated = false;

  constructor() {
    this.destroyRef.onDestroy(() => {
      const elements = this.pillarCards().map((c) => c.nativeElement) ?? [];
      if (elements.length > 0) {
        gsap.killTweensOf(elements);
      }
    });
  }

  animateCards(): void {
    if (this.animated) return;
    this.animated = true;

    const cards = this.pillarCards().map((c) => c.nativeElement) ?? [];
    if (cards.length > 0) {
      gsap.fromTo(
        cards,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: 'power3.out',
        },
      );
    }
  }
}
